import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, getApiErrorMessage, type RentalPortalLink } from "../../api/client";
import { unwrapArray } from "../../lib/api-envelope";
import { formatInstant, formatInstantDay } from "../../lib/calendar-date";
import { LoadFailureNotice } from "../ui/LoadFailureNotice";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { DhEmpty, DhPanel } from "../../pages/downhole/DownholeBits";

export const portalLinksQueryKey = (orderId: string) => ["rental-portal-links", orderId] as const;

/** A link minted before per-person links (api#1235), or copied rather than emailed, has no recipient. */
const UNADDRESSED = "Not sent to a named person";

/**
 * WHO HOLDS A PORTAL LINK TO THIS ORDER (api#1235) — one row per person, with
 * a Revoke on each. Each send is tied to the address it went to, so revoking
 * one person's link never touches anyone else's.
 *
 * The API never returns the link itself here, only who has one. Rendered only
 * for the roles that can send and revoke (the endpoint's own gate).
 */
export default function PortalLinks({ orderId, timeZone }: { orderId: string; timeZone: string | null | undefined }) {
  const queryClient = useQueryClient();
  const [revoking, setRevoking] = useState<RentalPortalLink | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const linksQuery = useQuery({
    queryKey: portalLinksQueryKey(orderId),
    queryFn: async () => unwrapArray<RentalPortalLink>((await api.rentals.orders.portalLinks(orderId)).data),
    enabled: !!orderId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(linksQuery);
  const links = linksQuery.data ?? [];

  const who = (l: RentalPortalLink) => l.recipientEmail ?? UNADDRESSED;

  const handleRevoke = async () => {
    if (!revoking) return;
    setSubmitting(true);
    setRefusal(null);
    try {
      await api.rentals.orders.revokePortalLink(orderId, revoking.id);
      toast.success(revoking.recipientEmail ? `Portal link for ${revoking.recipientEmail} revoked` : "Portal link revoked");
      setRevoking(null);
      await queryClient.invalidateQueries({ queryKey: portalLinksQueryKey(orderId) });
    } catch (err) {
      setRefusal(getApiErrorMessage(err, "The link was not revoked."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DhPanel eyebrow="Customer access" title="Portal links" testId="rental-order-portal-links">
        {failure ? (
          <LoadFailureNotice error={queryFailureError(linksQuery)} kind={failure} subject="portal links" allowed="admins, office staff, supervisors and dispatchers" />
        ) : linksQuery.isLoading ? (
          <div className="p-4" aria-hidden="true"><div className="h-12 animate-pulse rounded-fi-control bg-fieldiq-dark/60" /></div>
        ) : links.length === 0 ? (
          <DhEmpty>No one has a link to this order.</DhEmpty>
        ) : (
          <ul className="divide-y divide-fieldiq-border">
            {links.map((l) => (
              <li key={l.id} data-testid="portal-link-row" className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm">
                <div className="min-w-0 space-y-0.5">
                  <p className={l.recipientEmail ? "break-all font-semibold text-fieldiq-text" : "text-fieldiq-muted"} data-testid="portal-link-recipient">
                    {who(l)}
                  </p>
                  <p className="text-xs text-fieldiq-muted">
                    <span data-testid="portal-link-sent">Sent {formatInstant(l.sentAt, timeZone) ?? "—"}</span>
                    {" · "}
                    <span data-testid="portal-link-opened">
                      {l.lastOpenedAt ? `Last opened ${formatInstant(l.lastOpenedAt, timeZone) ?? "—"}` : "Not opened yet"}
                    </span>
                    {" · "}
                    <span data-testid="portal-link-expires">
                      {l.expired ? "Expired" : "Expires"} {formatInstantDay(l.expiresAt, timeZone) ?? "—"}
                    </span>
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label={`Revoke portal link for ${who(l)}`}
                  onClick={() => { setRefusal(null); setRevoking(l); }}
                >
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DhPanel>

      <Dialog open={revoking !== null} onOpenChange={(o) => { if (!o && !submitting) setRevoking(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke portal link</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-fieldiq-text" data-testid="revoke-portal-link-text">
            {revoking?.recipientEmail
              ? <>The link sent to <strong className="break-all">{revoking.recipientEmail}</strong> stops opening immediately.</>
              : <>This link stops opening immediately.</>}{" "}
            Everyone else&apos;s link keeps working. You can send a new one at any time.
          </p>
          {refusal && <p role="alert" className="text-sm font-medium text-fieldiq-text">{refusal}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevoking(null)} disabled={submitting}>Cancel</Button>
            <Button onClick={handleRevoke} disabled={submitting}>
              {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Revoke link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
